import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../../components/admin/AdminShell';
import {
  useWAStats, useWACampaigns, useDeleteWACampaign, useSendWACampaign,
  useWAAnalytics, useWATemplates, useSyncWATemplates, useWAInbox
} from '../../../hooks/useWhatsAppQueries';
import { useQuery } from '@tanstack/react-query';
import api from '../../../lib/api';
import { toast } from 'sonner';
import {
  Plus, Send, Trash2, Eye, RefreshCw, CheckCheck, XCircle, BarChart3,
  RotateCw, FileText, Inbox, Ban, MessageCircle, Calendar, TrendingUp,
  ShieldCheck, AlertTriangle, Activity, Loader2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from '../../../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const STATUS_CONFIG = {
  draft:    { label: 'Draft',    color: 'bg-[rgba(255,255,255,0.06)] text-[#B9B9B9]' },
  sending:  { label: 'Sending',  color: 'bg-[rgba(212,168,67,0.15)] text-[#D4A843]' },
  sent:     { label: 'Sent',     color: 'bg-[rgba(74,222,128,0.12)] text-[#4ADE80]' },
  partial:  { label: 'Partial',  color: 'bg-[rgba(251,191,36,0.12)] text-[#FBB924]' },
  failed:   { label: 'Failed',   color: 'bg-[rgba(255,77,77,0.12)] text-[#FF4D4D]' },
};

const TPL_STATUS_COLOR = {
  APPROVED: 'bg-[rgba(74,222,128,0.12)] text-[#4ADE80]',
  PENDING:  'bg-[rgba(251,191,36,0.12)] text-[#FBB924]',
  REJECTED: 'bg-[rgba(255,77,77,0.12)] text-[#FF4D4D]',
  PAUSED:   'bg-[rgba(255,255,255,0.06)] text-[#B9B9B9]',
};

const CATEGORY_COLOR = {
  MARKETING:      'bg-[rgba(212,168,67,0.12)] text-[#D4A843]',
  UTILITY:        'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]',
  AUTHENTICATION: 'bg-[rgba(167,139,250,0.12)] text-[#A78BFA]',
};

function HealthCheckDialog() {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['wa-diagnostics'],
    queryFn:  () => api.get('/api/admin/marketing/whatsapp/diagnostics'),
    select:   (res) => res.data,
    enabled:  open,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="gap-2 bg-transparent border-[rgba(255,255,255,0.12)] text-[#B9B9B9] hover:text-[#25D366] hover:border-[rgba(37,211,102,0.4)]"
        data-testid="wa-health-check-btn"
      >
        <ShieldCheck size={14}/> Health Check
      </Button>
      <DialogContent
        data-testid="wa-health-check-dialog"
        className="bg-[#0A0A0A] border border-[rgba(255,255,255,0.08)] text-[#F2F2F2] max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#F2F2F2]">
            <Activity size={18} className="text-[#25D366]"/> WhatsApp Integration Health
          </DialogTitle>
          <DialogDescription className="text-[#B9B9B9]">
            Probes Meta Graph API to verify why sends might be silently dropped.
          </DialogDescription>
        </DialogHeader>

        {q.isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={22} className="animate-spin text-[#25D366]"/>
          </div>
        )}

        {q.isError && (
          <div className="p-4 rounded-xl bg-[rgba(255,77,77,0.08)] border border-[rgba(255,77,77,0.3)] text-sm text-[#FF8A8A]">
            Could not run diagnostics: {q.error?.response?.data?.detail || q.error?.message}
          </div>
        )}

        {d && (
          <div className="space-y-4 text-sm">
            {/* Overall verdict */}
            <div
              data-testid="wa-health-verdict"
              className={`p-4 rounded-xl border ${
                d.ok
                  ? 'bg-[rgba(74,222,128,0.06)] border-[rgba(74,222,128,0.3)]'
                  : 'bg-[rgba(255,77,77,0.06)] border-[rgba(255,77,77,0.3)]'
              }`}
            >
              <div className="flex items-center gap-2">
                {d.ok
                  ? <CheckCheck size={18} className="text-[#4ADE80]"/>
                  : <AlertTriangle size={18} className="text-[#FF4D4D]"/>}
                <span className={`font-semibold ${d.ok ? 'text-[#4ADE80]' : 'text-[#FF8A8A]'}`}>
                  {d.ok ? 'All systems operational' : `${d.issues.length} issue${d.issues.length === 1 ? '' : 's'} detected`}
                </span>
              </div>
              {!d.ok && (
                <ul className="mt-2 space-y-1.5 text-xs text-[#FF8A8A] list-disc list-inside">
                  {d.issues.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              )}
            </div>

            {/* Phone number */}
            {d.phone_number && (
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.07)]">
                <p className="text-[11px] text-[#6B6B6B] uppercase tracking-widest mb-2">Phone Number</p>
                <p className="text-[#F2F2F2] font-mono text-sm">{d.phone_number.display_phone_number}</p>
                <p className="text-xs text-[#B9B9B9] mt-1">{d.phone_number.verified_name}</p>
                <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                  <Badge variant="outline" className={`${
                    d.phone_number.quality_rating === 'GREEN'
                      ? 'border-[rgba(74,222,128,0.3)] text-[#4ADE80]'
                      : 'border-[rgba(251,191,36,0.3)] text-[#FBB924]'
                  }`}>
                    Quality: {d.phone_number.quality_rating}
                  </Badge>
                  <Badge variant="outline" className="border-[rgba(96,165,250,0.3)] text-[#60A5FA]">
                    {d.phone_number.throughput?.level || 'STANDARD'}
                  </Badge>
                  <Badge variant="outline" className={`${
                    d.phone_number.code_verification_status === 'VERIFIED'
                      ? 'border-[rgba(74,222,128,0.3)] text-[#4ADE80]'
                      : 'border-[rgba(251,191,36,0.3)] text-[#FBB924]'
                  }`}>
                    {d.phone_number.code_verification_status}
                  </Badge>
                </div>
              </div>
            )}

            {/* WABA */}
            {d.waba && (
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.07)]">
                <p className="text-[11px] text-[#6B6B6B] uppercase tracking-widest mb-2">WABA</p>
                <p className="text-[#F2F2F2] text-sm">{d.waba.name}</p>
                <p className="text-[11px] text-[#6B6B6B] mt-1 font-mono">id: {d.waba.id}</p>
                <Badge
                  variant="outline"
                  className={`mt-3 ${
                    d.waba.account_review_status === 'APPROVED'
                      ? 'border-[rgba(74,222,128,0.3)] text-[#4ADE80]'
                      : 'border-[rgba(251,191,36,0.3)] text-[#FBB924]'
                  }`}
                >
                  Review: {d.waba.account_review_status}
                </Badge>
              </div>
            )}

            {/* Meta-side authoritative Health Status */}
            {d.health && (
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.07)]">
                <p className="text-[11px] text-[#6B6B6B] uppercase tracking-widest mb-2">Meta Send Capability</p>
                <Badge
                  variant="outline"
                  className={`${
                    (d.health.can_send_message || '').toUpperCase() === 'AVAILABLE'
                      ? 'border-[rgba(74,222,128,0.3)] text-[#4ADE80]'
                      : 'border-[rgba(251,191,36,0.4)] text-[#FBB924]'
                  }`}
                >
                  {d.health.can_send_message}
                </Badge>
                <p className="text-[11px] text-[#6B6B6B] mt-2">
                  Per-entity status (PHONE/WABA/BUSINESS/APP) — Meta's own verdict on whether
                  your sends will actually reach recipients.
                </p>
              </div>
            )}

            {/* Last 24h Analytics from Meta — the ground truth for delivery */}
            {d.analytics_24h && (
              <div className="p-4 rounded-xl bg-[rgba(96,165,250,0.05)] border border-[rgba(96,165,250,0.2)]">
                <p className="text-[11px] text-[#60A5FA] uppercase tracking-widest mb-2">
                  Meta Analytics · Last 24 h
                </p>
                <div className="flex items-baseline gap-6 text-sm">
                  <div>
                    <span className="text-[#6B6B6B] text-xs">Sent: </span>
                    <strong className="text-[#F2F2F2] text-lg font-mono">{d.analytics_24h.sent}</strong>
                  </div>
                  <div>
                    <span className="text-[#6B6B6B] text-xs">Delivered: </span>
                    <strong className="text-[#4ADE80] text-lg font-mono">{d.analytics_24h.delivered}</strong>
                  </div>
                  {d.analytics_24h.sent > 0 && (
                    <div>
                      <span className="text-[#6B6B6B] text-xs">Rate: </span>
                      <strong className="text-[#60A5FA] font-mono">
                        {Math.round((d.analytics_24h.delivered / d.analytics_24h.sent) * 100)}%
                      </strong>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-[#6B6B6B] mt-2">
                  This is Meta's own counter. If &quot;Delivered&quot; here is non-zero but our
                  internal counter shows 0, the webhook subscription is missing — Meta is delivering
                  but not telling us.
                </p>
              </div>
            )}

            {/* Subscribed apps */}
            <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.07)]">
              <p className="text-[11px] text-[#6B6B6B] uppercase tracking-widest mb-2">Webhook Subscription</p>
              {(d.subscribed_apps || []).length === 0 ? (
                <p className="text-xs text-[#FF8A8A] flex items-center gap-1.5">
                  <AlertTriangle size={12}/> No app subscribed — Meta will NOT send status callbacks.
                </p>
              ) : (
                <div className="space-y-2">
                  {d.subscribed_apps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <CheckCheck size={12} className="text-[#4ADE80]"/>
                      <span className="text-[#F2F2F2]">{s.whatsapp_business_api_data?.name}</span>
                      <code className="text-[#6B6B6B]">{s.whatsapp_business_api_data?.id}</code>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Webhook activity */}
            <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.07)]">
              <p className="text-[11px] text-[#6B6B6B] uppercase tracking-widest mb-2">Recent Webhook Activity</p>
              <p className="text-xs text-[#B9B9B9]">
                Total events received: <strong className="text-[#F2F2F2]">{d.webhook_activity?.total_events ?? 0}</strong>
              </p>
              {d.webhook_activity?.last_event && (
                <p className="text-xs text-[#6B6B6B] mt-1">
                  Last: <code className="text-[#D4A843]">{d.webhook_activity.last_event}</code>
                  {' @ '}{new Date(d.webhook_activity.last_event_at).toLocaleString()}
                </p>
              )}
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => q.refetch()}
              disabled={q.isFetching}
              className="gap-2 bg-transparent border-[rgba(255,255,255,0.12)] text-[#B9B9B9]"
              data-testid="wa-health-refresh-btn"
            >
              {q.isFetching
                ? <Loader2 size={13} className="animate-spin"/>
                : <RotateCw size={13}/>} Re-run probe
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-6 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#B9B9B9] uppercase tracking-widest">{label}</p>
        <div className="p-2 rounded-lg bg-[rgba(37,211,102,0.1)]">
          <Icon size={16} className="text-[#25D366]" />
        </div>
      </div>
      <p className="text-3xl font-bold text-[#F2F2F2] font-mono">{value ?? '—'}</p>
      {sub && <p className="text-xs text-[#6B6B6B] mt-1">{sub}</p>}
    </Card>
  );
}

function DeliveryTrendChart({ data }) {
  return (
    <Card className="p-5 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-[#25D366]" />
          <h3 className="text-sm text-[#F2F2F2] font-semibold uppercase tracking-widest">Delivery Trend</h3>
        </div>
        <p className="text-xs text-[#6B6B6B]">Last 30 days</p>
      </div>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={data || []} margin={{ top: 5, right: 12, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#6B6B6B', fontSize: 10 }}
              tickFormatter={(d) => d?.slice(5) || ''}
              minTickGap={20}
              stroke="rgba(255,255,255,0.1)"
            />
            <YAxis tick={{ fill: '#6B6B6B', fontSize: 10 }} stroke="rgba(255,255,255,0.1)" allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: '#0F0F0F',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                fontSize: 12,
                color: '#F2F2F2',
              }}
              labelStyle={{ color: '#B9B9B9' }}
            />
            <Line type="monotone" dataKey="sent"      stroke="#25D366" strokeWidth={2} dot={false} name="Sent" />
            <Line type="monotone" dataKey="delivered" stroke="#60A5FA" strokeWidth={2} dot={false} name="Delivered" />
            <Line type="monotone" dataKey="read"      stroke="#D4A843" strokeWidth={2} dot={false} name="Read" />
            <Line type="monotone" dataKey="failed"    stroke="#FF4D4D" strokeWidth={2} dot={false} name="Failed" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[#B9B9B9]">
        <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-[#25D366]"/>Sent</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-[#60A5FA]"/>Delivered</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-[#D4A843]"/>Read</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-[#FF4D4D]"/>Failed</span>
      </div>
    </Card>
  );
}

function StatusMixDonut({ mix }) {
  const data = [
    { name: 'Delivered', value: Math.max(0, (mix?.delivered || 0) - (mix?.read || 0)), color: '#60A5FA' },
    { name: 'Read',      value: mix?.read || 0,    color: '#D4A843' },
    { name: 'Failed',    value: mix?.failed || 0,  color: '#FF4D4D' },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Card className="p-5 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={14} className="text-[#25D366]" />
        <h3 className="text-sm text-[#F2F2F2] font-semibold uppercase tracking-widest">Status Mix</h3>
      </div>
      {total === 0 ? (
        <div className="h-[240px] flex items-center justify-center">
          <p className="text-xs text-[#6B6B6B]">No data yet</p>
        </div>
      ) : (
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={3} stroke="none">
                {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: '#0F0F0F',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#F2F2F2',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, color: '#B9B9B9' }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function TopTemplatesPanel({ items }) {
  return (
    <Card className="p-5 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl" data-testid="wa-top-templates">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={14} className="text-[#25D366]" />
        <h3 className="text-sm text-[#F2F2F2] font-semibold uppercase tracking-widest">Top Templates</h3>
      </div>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-[#6B6B6B] py-6 text-center">No campaign sends yet</p>
      ) : (
        <div className="space-y-3">
          {items.map((t, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-[rgba(255,255,255,0.02)]">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#F2F2F2] truncate font-medium">{t.name}</p>
                <p className="text-[10px] text-[#6B6B6B] mt-0.5">{t.sent} sent · {t.delivery_rate}% delivered</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-[#25D366]">{t.delivered}</p>
                {t.failed > 0 && <p className="text-[10px] font-mono text-[#FF4D4D]">{t.failed} failed</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function TemplatesTab() {
  const { data: tplData, isLoading } = useWATemplates();
  const syncMutation = useSyncWATemplates();
  const templates = tplData?.items || [];

  const handleSync = async () => {
    try {
      const res = await syncMutation.mutateAsync();
      const r = res.data || res;
      toast.success(`Synced ${r.fetched} templates · ${r.created} created · ${r.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    }
  };

  return (
    <div className="space-y-5" data-testid="wa-templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#F2F2F2]">Approved Templates</h3>
          <p className="text-xs text-[#6B6B6B] mt-1">Synced directly from your Meta Business WABA</p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          className="gap-2 rounded-xl"
          style={{ background: '#25D366', color: '#000' }}
          data-testid="wa-template-sync-btn"
        >
          {syncMutation.isPending ? (
            <><RotateCw size={14} className="animate-spin" /> Syncing...</>
          ) : (
            <><RotateCw size={14} /> Sync from Meta</>
          )}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw size={20} className="animate-spin text-[#25D366]" />
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-56 gap-4 text-center">
          <FileText size={36} className="text-[#6B6B6B]" />
          <div>
            <p className="text-[#F2F2F2] font-semibold">No templates synced yet</p>
            <p className="text-[#6B6B6B] text-sm mt-1">Click "Sync from Meta" to fetch your approved templates</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[rgba(255,255,255,0.07)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
                <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Name</th>
                <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Language</th>
                <th className="px-5 py-3 text-center text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Category</th>
                <th className="px-5 py-3 text-center text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Status</th>
                <th className="px-5 py-3 text-center text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Variables</th>
                <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Preview</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t._id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                  <td className="px-5 py-3">
                    <code className="text-[#D4A843] text-xs">{t.name}</code>
                  </td>
                  <td className="px-5 py-3 text-[#B9B9B9] text-xs uppercase">{t.language}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-medium ${CATEGORY_COLOR[t.category] || 'bg-[rgba(255,255,255,0.06)] text-[#B9B9B9]'}`}>
                      {t.category || '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-medium ${TPL_STATUS_COLOR[t.status] || 'bg-[rgba(255,255,255,0.06)] text-[#B9B9B9]'}`}>
                      {t.status || '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center text-[#F2F2F2] font-mono text-xs">{t.variable_count ?? 0}</td>
                  <td className="px-5 py-3 max-w-md">
                    <p className="text-xs text-[#B9B9B9] truncate" title={t.body_preview}>{t.body_preview || '—'}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InboxTab() {
  const [onlyOptOut, setOnlyOptOut] = useState(false);
  const { data: inboxData, isLoading } = useWAInbox({ only_optout: onlyOptOut, limit: 50 });
  const items = inboxData?.items || [];

  return (
    <div className="space-y-5" data-testid="wa-inbox-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[#F2F2F2]">Customer Replies</h3>
          <p className="text-xs text-[#6B6B6B] mt-1">Inbound WhatsApp messages with auto opt-out tracking</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setOnlyOptOut(false)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              !onlyOptOut ? 'bg-[#25D366] text-black' : 'bg-[rgba(255,255,255,0.05)] text-[#B9B9B9]'
            }`}
          >
            All Replies
          </button>
          <button
            onClick={() => setOnlyOptOut(true)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              onlyOptOut ? 'bg-[#FF4D4D] text-white' : 'bg-[rgba(255,255,255,0.05)] text-[#B9B9B9]'
            }`}
          >
            <Ban size={11} className="inline mr-1" /> Opt-outs only
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw size={20} className="animate-spin text-[#25D366]" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-56 gap-4 text-center">
          <Inbox size={36} className="text-[#6B6B6B]" />
          <div>
            <p className="text-[#F2F2F2] font-semibold">No replies yet</p>
            <p className="text-[#6B6B6B] text-sm mt-1">
              {onlyOptOut ? 'No opt-out messages received' : 'Customer replies will appear here'}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => (
            <Card key={m._id} className="p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-xl" data-testid={`wa-inbox-row-${m.wa_message_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-xs text-[#D4A843]">{m.from_phone}</code>
                    {m.is_opt_out && (
                      <span className="px-2 py-0.5 rounded-full bg-[rgba(255,77,77,0.12)] text-[#FF4D4D] text-[10px] font-medium">
                        <Ban size={9} className="inline mr-1" />Auto Opt-Out
                      </span>
                    )}
                    <span className="text-[10px] text-[#6B6B6B] uppercase">{m.type}</span>
                  </div>
                  <p className="text-sm text-[#F2F2F2] break-words">{m.body || <span className="text-[#6B6B6B] italic">(no text content)</span>}</p>
                </div>
                <div className="text-xs text-[#6B6B6B] flex items-center gap-1 flex-shrink-0">
                  <Calendar size={11} />
                  {m.received_at ? new Date(m.received_at).toLocaleString() : '—'}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignsTab() {
  const navigate = useNavigate();
  const { data: campaignsData, isLoading } = useWACampaigns();
  const deleteCampaign = useDeleteWACampaign();
  const sendCampaign   = useSendWACampaign();
  const campaigns = campaignsData?.campaigns || [];
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = statusFilter === 'all' ? campaigns : campaigns.filter(c => c.status === statusFilter);

  const handleSend = async (id, name) => {
    try {
      await sendCampaign.mutateAsync(id);
      toast.success(`Sending "${name}"…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start campaign');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCampaign.mutateAsync(id);
      toast.success('Campaign deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#F2F2F2]">Campaigns</h3>
        <Button
          onClick={() => navigate('/admin/marketing/whatsapp/new')}
          className="gap-2 rounded-xl px-5"
          style={{ background: '#25D366', color: '#000' }}
          data-testid="wa-new-campaign-btn"
        >
          <Plus size={16} /> New Campaign
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['all', 'draft', 'sending', 'sent', 'partial', 'failed'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === s
                ? 'bg-[#25D366] text-black'
                : 'bg-[rgba(255,255,255,0.05)] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.09)]'
            }`}
          >
            {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw size={22} className="animate-spin text-[#25D366]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-56 gap-4 text-center">
          <MessageCircle size={36} className="text-[#25D366]" />
          <div>
            <p className="text-[#F2F2F2] font-semibold">No campaigns yet</p>
            <p className="text-[#6B6B6B] text-sm mt-1">Create your first WhatsApp campaign to get started</p>
          </div>
          <Button onClick={() => navigate('/admin/marketing/whatsapp/new')} style={{ background: '#25D366', color: '#000' }} className="gap-2 rounded-xl">
            <Plus size={15} /> Create Campaign
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[rgba(255,255,255,0.07)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
                <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Campaign</th>
                <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Template</th>
                <th className="px-5 py-3 text-center text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Status</th>
                <th className="px-5 py-3 text-right text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Sent</th>
                <th className="px-5 py-3 text-right text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Delivered</th>
                <th className="px-5 py-3 text-right text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Failed</th>
                <th className="px-5 py-3 text-center text-xs text-[#6B6B6B] uppercase tracking-widest font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
                return (
                  <tr key={c._id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-[#F2F2F2] font-medium">{c.name}</p>
                      <p className="text-xs text-[#6B6B6B] mt-0.5">{c.total_contacts || 0} contacts</p>
                    </td>
                    <td className="px-5 py-3">
                      <code className="text-xs text-[#D4A843] bg-[rgba(212,168,67,0.08)] px-2 py-0.5 rounded">{c.template_name}</code>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-[#F2F2F2]">{c.sent_count || 0}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#4ADE80]">{c.delivered_count || 0}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#FF4D4D]">{c.failed_count || 0}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/marketing/whatsapp/${c._id}`)} className="h-7 w-7 p-0 text-[#B9B9B9] hover:text-[#F2F2F2]" data-testid="wa-view-btn">
                          <Eye size={14} />
                        </Button>
                        {c.status === 'draft' && (
                          <Button size="sm" variant="ghost" onClick={() => handleSend(c._id, c.name)} disabled={sendCampaign.isPending} className="h-7 w-7 p-0 text-[#25D366] hover:text-[#25D366]" data-testid="wa-send-btn">
                            <Send size={14} />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#FF4D4D] hover:text-[#FF4D4D]">
                              <Trash2 size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#111] border border-[rgba(255,255,255,0.1)]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-[#F2F2F2]">Delete Campaign?</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#B9B9B9]">
                                This will permanently delete "{c.name}" and all its message logs.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-[rgba(255,255,255,0.1)] text-[#B9B9B9]">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(c._id)} className="bg-[#FF4D4D] text-white">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function WhatsAppPage() {
  const { data: stats } = useWAStats();
  const { data: analytics } = useWAAnalytics(30);

  return (
    <AdminShell title="WhatsApp Campaigns">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#F2F2F2] flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.25)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
            </span>
            WhatsApp Marketing
          </h1>
          <p className="text-[#B9B9B9] mt-1">Meta WABA · Cloud API v19.0</p>
        </div>
        <HealthCheckDialog />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={BarChart3} label="Total Campaigns" value={stats?.total_campaigns ?? 0} />
        <KpiCard icon={Send}      label="Messages Sent"   value={stats?.total_sent ?? 0} />
        <KpiCard icon={CheckCheck} label="Delivered"      value={stats?.total_delivered ?? 0} sub={stats?.delivery_rate != null ? `${stats.delivery_rate}% rate` : null} />
        <KpiCard icon={XCircle}   label="Failed"          value={stats?.total_failed ?? 0} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.07)] mb-5" data-testid="wa-tabs">
          <TabsTrigger value="overview"  data-testid="wa-tab-overview"  className="data-[state=active]:bg-[#25D366] data-[state=active]:text-black">Overview</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="wa-tab-campaigns" className="data-[state=active]:bg-[#25D366] data-[state=active]:text-black">Campaigns</TabsTrigger>
          <TabsTrigger value="templates" data-testid="wa-tab-templates" className="data-[state=active]:bg-[#25D366] data-[state=active]:text-black">Templates</TabsTrigger>
          <TabsTrigger value="inbox"     data-testid="wa-tab-inbox"     className="data-[state=active]:bg-[#25D366] data-[state=active]:text-black">Inbox</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-5" data-testid="wa-overview-tab">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <DeliveryTrendChart data={analytics?.timeseries} />
            </div>
            <div>
              <StatusMixDonut mix={analytics?.status_mix} />
            </div>
          </div>
          <TopTemplatesPanel items={analytics?.top_templates} />
        </TabsContent>

        <TabsContent value="campaigns" data-testid="wa-campaigns-tab">
          <CampaignsTab />
        </TabsContent>

        <TabsContent value="templates">
          <TemplatesTab />
        </TabsContent>

        <TabsContent value="inbox">
          <InboxTab />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
